/**
 * Режим области сессии — что показывает переключатель «Хозяйство ▾» и под
 * что строится меню (AK-898; AK-866 В-2/В-3, макет v2 экраны 1 и 4).
 *
 *   · `farm`    — выбрано хозяйство (или отделение): меню хозяйства;
 *   · `cluster` — в переключателе кластер: меню хозяйства, экраны по
 *                 хозяйству просят выбрать его (`FarmScopeNotice`);
 *   · `holding` — «Холдинг»: Холдинг · Справочники · ⚙ Компания — уровень
 *                 компании = только общая аналитика (В-2), сквозных списков
 *                 нет;
 *   · `none`    — `/scope/available` пуст (компания ещё не заведена): меню
 *                 хозяйства по умолчанию, `/dashboard` сам уводит в онбординг.
 *
 * Правило ТО ЖЕ, что у подписи `FarmSwitcher`: выбирать не из чего — режим
 * по единственному варианту, а не по тому, что случайно лежит в сессии
 * (кластер у члена кластера с одним хозяйством). Иначе кнопка писала бы
 * «Хозяйство: Ромашка», а меню было бы «кластерным».
 *
 * Хук только читает общий кэш `/scope/available` (`useAvailableScopes`), сам
 * ничего не пишет и не переключает.
 */
import { useAvailableScopes } from './useAvailableScopes';
import type { AvailableScopes, ScopeInfo } from './ScopeTree';

export type ScopeMode = 'holding' | 'cluster' | 'farm' | 'none';

export interface ScopeModeState {
  mode: ScopeMode;
  status: 'loading' | 'ready' | 'error';
  /** Текущая строка области, когда список загружен. */
  current: ScopeInfo | null;
}

function modeOfCurrent(s: ScopeInfo): ScopeMode {
  if (s.scope_type === 'farm' || s.scope_type === 'brigade') return 'farm';
  if (s.scope_type === 'cluster') return 'cluster';
  return 'holding';
}

/** Чистое правило — тестируется без react-query. */
export function scopeModeOf(data: AvailableScopes | undefined): ScopeMode {
  if (!data || !Array.isArray(data.available) || !data.current) return 'none';
  const farms = data.available.filter((s) => s.scope_type === 'farm');
  const companyRow = data.available.find((s) => s.scope_type === 'company');
  const optionsCount = farms.length + (companyRow ? 1 : 0);
  if (optionsCount === 0) return 'none';
  if (optionsCount === 1) return companyRow ? 'holding' : 'farm';
  return modeOfCurrent(data.current);
}

export function useScopeMode(): ScopeModeState {
  const scopes = useAvailableScopes();
  const raw = scopes.data;
  const data = raw && Array.isArray(raw.available) && raw.current ? raw : undefined;
  if (data) return { mode: scopeModeOf(data), status: 'ready', current: data.current };
  // Ответ пришёл, но не того формата (заглушка) — считаем «переключать нечего».
  if (raw) return { mode: 'none', status: 'ready', current: null };
  if (scopes.isError) return { mode: 'none', status: 'error', current: null };
  return { mode: 'none', status: 'loading', current: null };
}
